import { SparklesIcon } from 'lucide-react';
import React from 'react';
import ImageCard from './ImageCard';

// Define types for our section data
interface SectionImage {
  description: string;
  title: string;
  path: string;
}

interface Section {
  id: string;
  number: string;
  title: string;
  description: string;
  features: string[];
  images: SectionImage[];
  link?: string;
  linkText?: string;
}

const HowItWorks: React.FC = () => {
  // Sample data for the three sections
  const sections: Section[] = [
    {
      id: 'build',
      number: '01.',
      title: 'Build your ideal integration experience',
      description: 'Create a custom-branded integration marketplace, with a combination of native experience and powerful workflows builder',
      features: [
        'White-labeled Marketplace',
        'Dynamic Mapping',
        'Bi-directional Sync',
        'Headless UI/UX',
        'Requisition & Prioritization Tools',
        'Pre-built Templates',
        'Workflow Triggers & Actions'
      ],
      link: '/integrations',
      linkText: 'View all integrations',
      images: [
        { title: "1,000+ Integrations", path: 'https://cobalt-app-logos.s3.ap-south-1.amazonaws.com/website_images/Home+page-+Images/1%2C000%2B+Integrations.svg', description: 'White-label your integration marketplace, engage users, collect upvotes, and prioritize development based on demand.' },
        { title: "Headless UI", path: 'https://cobalt-app-logos.s3.ap-south-1.amazonaws.com/website_images/Home+page-+Images/Headless+UI.svg', description: 'On-brand integrations that look and feel hand-in glove to your in-product user experience with UI abstraction' },
        { title: "Workflow engine", path: 'https://cobalt-app-logos.s3.ap-south-1.amazonaws.com/website_images/Home+page-+Images/Workflow+engine.svg', description: "Empower teams to build integrations confidently using Cobalt's low-code orchestration builder for seamless data." }
      ]
    },
    {
      id: 'deploy',
      number: '02.',
      title: 'Deploy apps with scale and reliability',
      description: 'Scale with confidence and free of stress as Cobalt takes care of integration infrastructure for you',
      features: [
        'Production & Staging Env.',
        'Version & Release Control',
        'CI/CD pipeline',
        'Built-in Retries at Nodes',
        'Real-time Testing',
        'Detailed Audit Logging',
        'Queue Management',
        'Rate Limits Management'
      ],
      link: 'https://docs.gocobalt.io/deployment/overview',
      linkText: 'Learn more',
      images: [
        { title: "Integration development lifecycle", path: 'https://cobalt-app-logos.s3.ap-south-1.amazonaws.com/website_images/Home+page-+Images/Integration+development+lifecycle.svg', description: 'Test integrations quickly using scenario simulations. Manage versions effortlessly with release cycles & staging environments.' },
        { title: "Handle data at scale", path: 'https://cobalt-app-logos.s3.ap-south-1.amazonaws.com/website_images/Home+page-+Images/Handle+data+at+scale.svg', description: 'Process millions of API calls for your users with advanced features like built-in retries, customizable rate limits, and robust queuing systems.' }
      ]
    },
    {
      id: 'maintain',
      number: '03.',
      title: 'Continuous support & maintenance',
      description: 'You never have to worry about API maintenance, new development and custom use cases any more',
      features: [
        'Real-time Monitoring',
        'Customer Usage Analytics',
        'API Maintenance',
        'Integration Performance Metrics',
        'Changelogs',
        'Workflow Support',
        'Bespoke Integrations'
      ],
      link: 'https://docs.gocobalt.io/api-reference/overview',
      linkText: 'Learn more',
      images: [
        { title: "Real-time observability", path: 'https://cobalt-app-logos.s3.ap-south-1.amazonaws.com/website_images/Home+page-+Images/Real-time+observability.svg', description: 'Performance monitoring, detailed debugging logs, and usage tracking. Analyze customer insights to drive adoption for better outcomes.' },
        { title: "API maintenance", path: 'https://cobalt-app-logos.s3.ap-south-1.amazonaws.com/website_images/Home+page-+Images/API+maintenance.svg', description: 'Cobalt takes care of app maintenance, API deprecations, changelogs, and ensures seamless connectivity.' },
        { title: "Continuous support", path: 'https://cobalt-app-logos.s3.ap-south-1.amazonaws.com/website_images/Home+page-+Images/Continuous+support.svg', description: "Experience rapid value realization with Cobalt's continuous support for workflow, and bespoke integration solutions." }
      ]
    }
  ];

  return (
    <section className="py-16 md:py-24">
      <div className="container mx-auto px-4">
        {/* Header */}
       <div className="text-center max-w-4xl mx-auto mb-16">
          <span className="inline-block text-sm font-semibold text-primary-600 tracking-wider mb-4">
            HOW IT WORKS
          </span>
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
            Drive your integration roadmap with end-to-end orchestration and support
          </h2>
          <p className="text-lg text-gray-600">
            Cobalt provides all the tools you need across the integration lifecycle from designing specs to fine-tuning integration performance
          </p>
        </div>

        <div className="relative">
          {sections.map((section, index) => (
            <div
              key={section.id}
              className="relative min-h-screen"
              id={section.id}
            >
              {/* Header - sticky only on desktop */}
              <div
                className="container mx-auto px-4 md:px-0 pt-4 lg:sticky lg:top-16 z-10 bg-gray-50 transition-transform duration-300"
                style={{
                  zIndex: 10 - index,
                }}
              >
                <div className="border bg-primary-50 text-primary-600 px-3 md:px-4 rounded-lg py-3 md:py-4 border-primary-400 mx-auto">
                  <div className="flex flex-row justify-between items-center gap-2 md:gap-0">
                    <div className="text-sm font-bold flex items-center gap-2">
                      <SparklesIcon className="w-4 h-4" /> {section.id.toUpperCase()}
                    </div>
                    <div className="text-sm font-bold">
                      <span className="hidden md:inline">HOW IT WORKS </span>{section.number.toUpperCase()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Section content */}
              <div className="container mx-auto pb-16 md:pb-32 max-h-fit px-4 md:px-0">
                <div className="grid pt-6 md:pt-10 grid-cols-1 lg:grid-cols-2 lg:gap-16 lg:border-l-4 border-primary-50 pl-2 lg:pl-8">
                  {/* Left column - static content on desktop, scrollable on mobile */}
                  <div className="lg:sticky lg:top-44 self-start max-h-none lg:max-h-[calc(100vh-6rem)] overflow-y-auto">
                    <h2 className="text-2xl md:text-3xl font-bold mb-4 md:mb-6">{section.title}</h2>
                    <p className="text-gray-600 mb-6 md:mb-8 max-w-xl">{section.description}</p>

                    <div className="grid text-sm md:text-base grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                      {section.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center">
                          <SparklesIcon className="text-primary-600 w-4 h-4 mr-2 flex-shrink-0" />
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>

                    <button className="mt-6 md:mt-8 font-bold border border-gray-300 rounded-lg px-4 md:px-6 py-2.5 md:py-3 text-sm w-full md:w-auto">
                      {section.link ? (
                        section.id === 'build' ? (
                          <a href={section.link}>
                            {section.linkText}
                          </a>
                        ) : (
                          <a href={section.link} target="_blank" rel="noopener noreferrer">
                            {section.linkText}
                          </a>
                        )
                      ) : (
                        section.linkText || 'Learn more'
                      )}
                    </button>
                  </div>

                  {/* Right column - scrolling content */}
                  <div className="flex flex-col gap-8 lg:gap-y-52 w-full lg:w-[95%] lg:ml-auto">
                    {section.images.map((image, idx) => (
                      <ImageCard
                        key={`${section.id}-image-${idx}`}
                        title={image.title}
                        imagePath={image.path}
                        description={image.description}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;